<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Deals" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <div class="shine" v-if="!isReadOnlyAccess">
            <button class="gold-background closed-deal-button" @click="goToDealWizard">
              <span class="closed-deal-button-text font-weight-bold">We have Closed a Deal!</span>
              <i></i>
            </button>
          </div>
        </template>
      </SimplePageHeader>
      <DealFilters
        class="px-5 mb-sm-5"
        :dealsClients="dealsClients"
        :dealsConsultants="dealsConsultants"
        :dealsJobTitles="dealsJobTitles"
        :dealsCreatedBy="dealsCreatedBy"
        @search="handleSearchClick"
        @clear="handleSearchClick"
      ></DealFilters>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="dealList"
          class="elevation-2"
        >
          <template v-slot:item.dealAssignmentType="{ item }">
            {{ item.dealAssignmentType | getDealAssignmentTypeLabel }}
          </template>
          <template v-slot:item.dealStatusName="{ item }">
            <span :class="{
              'text-in-process' : item.statusId === dealStatuses.InProcess,
              'text-new': item.statusId === dealStatuses.New,
              'text-rejected': item.statusId === dealStatuses.Rejected,
              'text-approved': item.statusId === dealStatuses.Approved
            }">{{ item.dealStatusName }}</span>
          </template>
          <template v-slot:item.createAt="{ item }">
            {{ item.createAt | formatDate }}
          </template>
          <template v-slot:item.startDate="{ item }">
            {{ item.startDate | formatDate }}
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              v-if="item.statusId === dealStatuses.Approved ||
              (item.statusId === dealStatuses.New && !isAdmin)||
              isReadOnlyAccess"
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editDeal($event, item)"
            >
              <v-icon color="primary">visibility</v-icon>
            </v-btn>
            <v-btn
              v-if="item.statusId !== dealStatuses.Approved &&
                  !(item.statusId === dealStatuses.New && !isAdmin) &&
                  !isReadOnlyAccess"
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editDeal($event, item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn
              v-if="isAdmin"
              class="mx-1 my-1"
              color="red"
              outlined
              @click="deleteDealHandler(item)"
            >
              <v-icon color="red">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <ConfirmationModal
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected deal?'"
      :toolbarColor="'primary'"
      @cancel-click="() => {
        this.showDeleteConfirmationDialog = false
        this.dealIdToDelete = null
      }"
      @ok-click="confirmDealDelete"
      :showModal="showDeleteConfirmationDialog"
    ></ConfirmationModal>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { dealWizardRoute } from '@/lib/constants/routes'
import DealFilters from './DealFilters'
import { ButtonAdd, BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { pageableAndSortable } from '@/lib/pagingAndSorting'
import { dealStatuses } from '@/lib/enum/dealStatuses'
import { ConfirmationModal } from '@/components/dialogs'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { userIsAdmin, getCurrentUser, userHasRoles } from '@/lib/authentication'

export default {
  mixins: [
    pageableAndSortable
  ],
  components: {
    BottomSpacer,
    DealFilters,
    ButtonAdd,
    ButtonCustomIcon,
    SimplePageHeader,
    ConfirmationModal
  },
  data () {
    return {
      dealStatuses,
      dealWizardRoute,
      showDeleteConfirmationDialog: false,
      dealIdToDelete: null,
      headers: [
        { text: 'Client', value: 'dealClientName', width: '11%', align: 'center', class: 'px-1', cellClass: 'pl-2 pr-1 text-start' },
        { text: 'Consultant', value: 'consultantName', width: '11%', align: 'center', class: 'px-1', cellClass: 'pl-2 pr-1 text-start' },
        { text: 'Job Title', value: 'jobTitle', width: '11%', align: 'center', class: 'px-1', cellClass: 'pl-2 pr-1 text-start' },
        { text: 'Start Date', value: 'startDate', width: '8%', class: 'px-1', cellClass: 'px-1' },
        { text: 'Type', value: 'dealType', width: '5%', align: 'center', class: 'px-1', cellClass: 'px-1' },
        { text: 'Status', value: 'dealStatusName', width: '11%', align: 'center', class: 'px-1', cellClass: 'px-1' },
        { text: 'Created by', value: 'createdByName', width: '11%', align: 'center', class: 'px-1', cellClass: 'pl-2 pr-1 text-start' },
        { text: 'Created On', value: 'createAt', width: '8%', align: 'center', class: 'px-1', cellClass: 'px-1' },
        { text: 'Actions', value: 'action', align: 'center', width: '23%', sortable: false, class: 'px-1', cellClass: 'px-1' }
      ],
      dealsClients: [],
      dealsConsultants: [],
      dealsJobTitles: [],
      dealsCreatedBy: []
    }
  },
  computed: {
    ...mapFields('deals', [
      'dealList'
    ]),
    isApproved () {
      return this.statusId === dealStatuses.Approved
    },
    isNewDeal () {
      return this.statusId === dealStatuses.New
    },
    isReadOnlyAccess () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    },
    isAdmin () {
      return this.userIsAdmin()
    }
  },
  methods: {
    getCurrentUser,
    userHasRoles,
    userIsAdmin,
    ...mapActions('deal', ['deleteDeal']),
    ...mapActions('deals', [
      'getFieldsWithFilters'
    ]),
    editDeal (event, deal) {
      if (event.ctrlKey === true) {
        let route = this.$router.resolve({ name: dealWizardRoute.name, params: { id: deal.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: dealWizardRoute.name, params: { id: deal.id } })
      }
    },
    handleSearchClick () {
      this.getFieldsWithFilters()
    },
    goToDealWizard () {
      this.$router.push({ name: dealWizardRoute.name })
    },
    deleteDealHandler (item) {
      this.dealIdToDelete = item.id
      this.showDeleteConfirmationDialog = true
    },
    confirmDealDelete () {
      this.deleteDeal(this.dealIdToDelete).then(() => {
        this.showDeleteConfirmationDialog = false
        this.getFieldsWithFilters()
      })
    }
  },
  mounted () {
    this.getFieldsWithFilters().then((deals) => {
      this.dealsClients = deals.map((deal) => { return { clientName: deal.dealClientName } })
      this.dealsClients = [...new Map(this.dealsClients.map(client => [client.clientName, client])).values()]
      this.dealsConsultants = deals.map((deal) => { return { consultantName: deal.consultantName } })
      this.dealsConsultants = [...new Map(this.dealsConsultants.map(consultant => [consultant.consultantName, consultant])).values()]
      this.dealsJobTitles = deals.map((deal) => { return { jobTitle: deal.jobTitle } })
      this.dealsJobTitles = [...new Map(this.dealsJobTitles.map(jobTitle => [jobTitle.jobTitle, jobTitle])).values()]
      this.dealsJobTitles = this.dealsJobTitles.filter(deal => {
        if (deal.jobTitle !== null && deal.jobTitle !== '') {
          return true
        }
        return false
      })
      this.dealsCreatedBy = deals.map((deal) => { return { id: deal.createdBy, createdByName: deal.createdByName } })
      this.dealsCreatedBy = [...new Map(this.dealsCreatedBy.map(createdBy => [createdBy.id, createdBy])).values()]
      this.dealsClients.sort((a, b) => (a.clientName > b.clientName) ? 1 : -1)
      this.dealsConsultants.sort((a, b) => (a.consultantName > b.consultantName) ? 1 : -1)
      this.dealsJobTitles.sort((a, b) => (a.jobTitle > b.jobTitle) ? 1 : -1)
      this.dealsCreatedBy.sort((a, b) => (a.createdByName > b.createdByName) ? 1 : -1)
    })
  }
}
</script>
<style>
.text-in-process {
  color: blue;
  border: 1px solid blue;
  border-radius: 10px;
  padding: 1px 0px 1px 0px;
  display: block;
  text-align: center;
}

.text-new {
  color: black;
  font-weight: bold;
  letter-spacing: 1px;
  border: 1px solid #ffd24d;
  background-color: #ffd24d;
  border-radius: 10px;
  padding: 1px 0px 1px 0px;
  display: block;
  text-align: center;
}

.text-rejected {
  color: red;
  border: 1px solid red;
  border-radius: 10px;
  padding: 1px 0px 1px 0px;
  display: block;
  text-align: center;
}

.text-approved {
  color: rgb(64, 153, 47);
  border: 1px solid rgb(64, 153, 47);
  border-radius: 10px;
  padding: 1px 0px 1px 0px;
  display: block;
  text-align: center;
}

</style>
